/**
 * Kit de interface comum as telas dos modulos. Existe para as telas feitas
 * em worktrees diferentes terem a mesma cara: selo de status, tabela que
 * vira lista no celular, painel lateral de edicao, campo com erro, busca.
 * Cor so por token (ui.css); nenhum componente aqui conhece modulo.
 */
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import './ui.css';

export type Tom = 'neutro' | 'info' | 'ok' | 'aviso' | 'erro' | 'marca';

/** Selo de status ("A conferir", "Assinado", "Atrasado"). */
export function Selo({ tom = 'neutro', children }: { tom?: Tom; children: ReactNode }) {
  return <span className={`selo selo--${tom}`}>{children}</span>;
}

export function Botao({
  variante = 'secundario', tipo = 'button', desativado, aoClicar, children, titulo,
}: {
  variante?: 'primario' | 'secundario' | 'perigo' | 'fantasma';
  tipo?: 'button' | 'submit';
  desativado?: boolean;
  aoClicar?: () => void;
  titulo?: string;
  children: ReactNode;
}) {
  return (
    <button
      type={tipo} className={`ui-botao ui-botao--${variante}`} disabled={desativado}
      onClick={aoClicar} {...(titulo !== undefined ? { title: titulo } : {})}
    >{children}</button>
  );
}

/** Campo de formulario com rotulo, dica e erro embaixo. */
export function Campo({
  rotulo, erro, dica, largura = 'inteira', children,
}: {
  rotulo: string;
  erro?: string | undefined;
  dica?: string;
  largura?: 'inteira' | 'metade' | 'terco';
  children: (id: string) => ReactNode;
}) {
  const id = useId();
  return (
    <div className={`ui-campo ui-campo--${largura}${erro !== undefined ? ' ui-campo--erro' : ''}`}>
      <label className="ui-campo__rotulo" htmlFor={id}>{rotulo}</label>
      {children(id)}
      {erro !== undefined
        ? <span className="ui-campo__erro" role="alert">{erro}</span>
        : dica !== undefined && <span className="ui-campo__dica">{dica}</span>}
    </div>
  );
}

/** Grade de campos do formulario: duas colunas no desktop, uma no celular. */
export function Formulario({ aoEnviar, children }: {
  aoEnviar: () => void; children: ReactNode;
}) {
  return (
    <form className="ui-form" noValidate
      onSubmit={(e) => { e.preventDefault(); aoEnviar(); }}>
      {children}
    </form>
  );
}

/** Busca com espera curta antes de disparar (nao chama a API a cada tecla). */
export function Busca({ valor, aoMudar, dica }: {
  valor: string; aoMudar: (v: string) => void; dica: string;
}) {
  const [local, setLocal] = useState(valor);
  useEffect(() => {
    const t = setTimeout(() => { if (local !== valor) aoMudar(local); }, 300);
    return () => clearTimeout(t);
  }, [local, valor, aoMudar]);
  return (
    <label className="ui-busca">
      <Search size={16} strokeWidth={1.75} aria-hidden="true" />
      <input type="search" value={local} placeholder={dica} aria-label={dica}
        onChange={(e) => setLocal(e.target.value)} />
    </label>
  );
}

export type Coluna<T> = {
  readonly id: string;
  readonly titulo: string;
  readonly celula: (linha: T) => ReactNode;
  /** Coluna que some no celular (a lista mostra so as principais). */
  readonly secundaria?: boolean;
  readonly alinhar?: 'direita';
};

/** Tabela no desktop; no celular cada linha vira um cartao. */
export function Tabela<T>({ colunas, linhas, chave, aoAbrir }: {
  colunas: readonly Coluna<T>[];
  linhas: readonly T[];
  chave: (linha: T) => string;
  aoAbrir?: (linha: T) => void;
}) {
  return (
    <div className="ui-tabela" role="table">
      <div className="ui-tabela__cabeca" role="row">
        {colunas.map((c) => (
          <span key={c.id} role="columnheader"
            className={`ui-tabela__th${c.secundaria === true ? ' ui-tabela--secundaria' : ''}${c.alinhar === 'direita' ? ' ui-tabela--direita' : ''}`}>
            {c.titulo}
          </span>
        ))}
      </div>
      {linhas.map((l) => (
        <div key={chave(l)} role="row"
          className={`ui-tabela__linha${aoAbrir !== undefined ? ' ui-tabela__linha--clicavel' : ''}`}
          {...(aoAbrir !== undefined ? {
            tabIndex: 0,
            onClick: () => aoAbrir(l),
            onKeyDown: (e: React.KeyboardEvent) => { if (e.key === 'Enter') aoAbrir(l); },
          } : {})}
        >
          {colunas.map((c) => (
            <span key={c.id} role="cell" data-titulo={c.titulo}
              className={`ui-tabela__td${c.secundaria === true ? ' ui-tabela--secundaria' : ''}${c.alinhar === 'direita' ? ' ui-tabela--direita' : ''}`}>
              {c.celula(l)}
            </span>
          ))}
        </div>
      ))}
    </div>
  );
}

/** Painel lateral de edicao ou detalhe. Fecha com Esc e pelo X. */
export function Painel({ titulo, subtitulo, aberto, aoFechar, rodape, children }: {
  titulo: string;
  subtitulo?: string;
  aberto: boolean;
  aoFechar: () => void;
  rodape?: ReactNode;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!aberto) return;
    const tecla = (e: KeyboardEvent) => { if (e.key === 'Escape') aoFechar(); };
    window.addEventListener('keydown', tecla);
    ref.current?.focus();
    return () => window.removeEventListener('keydown', tecla);
  }, [aberto, aoFechar]);
  if (!aberto) return null;
  return (
    <div className="ui-painel__fundo" onClick={aoFechar}>
      <div className="ui-painel" role="dialog" aria-modal="true" aria-label={titulo}
        tabIndex={-1} ref={ref} onClick={(e) => e.stopPropagation()}>
        <header className="ui-painel__cabeca">
          <div>
            <h2 className="ui-painel__titulo">{titulo}</h2>
            {subtitulo !== undefined && <p className="ui-painel__subtitulo">{subtitulo}</p>}
          </div>
          <button type="button" className="ui-painel__fechar" aria-label="Fechar"
            onClick={aoFechar}><X size={18} strokeWidth={1.75} /></button>
        </header>
        <div className="ui-painel__corpo">{children}</div>
        {rodape !== undefined && <footer className="ui-painel__rodape">{rodape}</footer>}
      </div>
    </div>
  );
}

/** Aviso dentro da tela (sucesso, erro de envio, informacao). */
export function Aviso({ tom = 'info', children }: { tom?: Tom; children: ReactNode }) {
  return <div className={`ui-aviso ui-aviso--${tom}`} role={tom === 'erro' ? 'alert' : 'status'}>{children}</div>;
}

/** Bloco do Inicio: titulo, numero em destaque e a lista curta. */
export function BlocoInicio({ titulo, numero, tom = 'marca', link, children }: {
  titulo: string;
  numero?: number;
  tom?: Tom;
  link?: { rotulo: string; href: string };
  children: ReactNode;
}) {
  return (
    <section className="ui-bloco">
      <header className="ui-bloco__cabeca">
        <h2 className="ui-bloco__titulo">{titulo}</h2>
        {numero !== undefined && <span className={`ui-bloco__numero selo selo--${tom}`}>{numero}</span>}
      </header>
      <div className="ui-bloco__corpo">{children}</div>
      {link !== undefined && <Link className="ui-bloco__link" to={link.href}>{link.rotulo}</Link>}
    </section>
  );
}

/** Barra acima da lista: busca e filtros a esquerda, acoes a direita. */
export function Barra({ children, acoes }: { children?: ReactNode; acoes?: ReactNode }) {
  return (
    <div className="ui-barra">
      <div className="ui-barra__filtros">{children}</div>
      {acoes !== undefined && <div className="ui-barra__acoes">{acoes}</div>}
    </div>
  );
}
